import React from "react";
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import TimerIcon from "@mui/icons-material/Timer";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EditIcon from "@mui/icons-material/Edit";
import VideocamIcon from "@mui/icons-material/Videocam";
import ReplayIcon from "@mui/icons-material/Replay";
import { PerformanceData } from "../types";
import { InfoTooltip } from "./InfoTooltip";

interface PerformancePanelProps {
  data: PerformanceData;
}

function formatSeconds(sec: number): string {
  if (!sec || !Number.isFinite(sec)) return "0s";
  if (sec < 60) return `${sec.toFixed(1)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return s > 0 ? `${m}m ${s}s` : `${m}m`;
}

const METRICS = [
  {
    key: "avgReportGenSec" as const,
    label: "Avg Report Generation",
    description: "Median end-to-end time from job creation to completed report artifact (p50 across all stations).",
    icon: <TimerIcon sx={{ fontSize: 20 }} />,
    color: "primary" as const,
    format: formatSeconds,
  },
  {
    key: "avgPdfGenSec" as const,
    label: "Avg PDF Generation",
    description: "Median time to render the final PDF document from the completed report (p50).",
    icon: <PictureAsPdfIcon sx={{ fontSize: 20 }} />,
    color: "info" as const,
    format: formatSeconds,
  },
  {
    key: "avgEditingTimeSec" as const,
    label: "Avg Editing Time",
    description: "Average WebSocket connection duration per session, representing how long users spend editing a report.",
    icon: <EditIcon sx={{ fontSize: 20 }} />,
    color: "secondary" as const,
    format: formatSeconds,
  },
  {
    key: "avgVideosPerReport" as const,
    label: "Avg Videos / Report",
    description: "Average number of bodycam video files attached per report generation job.",
    icon: <VideocamIcon sx={{ fontSize: 20 }} />,
    color: "success" as const,
    format: (v: number) => (!v || !Number.isFinite(v) ? "0" : v.toFixed(1)),
  },
  {
    key: "totalRetries" as const,
    label: "Total Retries",
    description: "Number of report generation jobs that were manually retried by users after an initial failure.",
    icon: <ReplayIcon sx={{ fontSize: 20 }} />,
    color: "warning" as const,
    format: (v: number) => String(v ?? 0),
  },
];

export const PerformancePanel: React.FC<PerformancePanelProps> = ({ data }) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: "100%",
        transition: "all 0.2s ease-in-out",
        "&:hover": { boxShadow: 2 },
      }}
    >
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Performance
            </Typography>
            <InfoTooltip title="Timing and volume metrics for the report generation pipeline. Durations use median (p50) to reduce outlier impact." />
          </Box>
        }
        subheader="Report generation performance metrics"
      />
      <CardContent>
        <Stack spacing={2.5}>
          {METRICS.map((m) => {
            const value = data[m.key];
            return (
              <Box
                key={m.key}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette[m.color].main, 0.04),
                  transition: "background-color 0.15s",
                  "&:hover": {
                    bgcolor: alpha(theme.palette[m.color].main, 0.08),
                  },
                }}
              >
                <Box
                  sx={{
                    color: `${m.color}.main`,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {m.icon}
                </Box>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    {m.label}
                  </Typography>
                  <InfoTooltip title={m.description} />
                </Box>
                <Typography
                  variant="body1"
                  sx={{
                    fontVariantNumeric: "tabular-nums",
                    fontWeight: 700,
                  }}
                  color={`${m.color}.main`}
                >
                  {m.format(value)}
                </Typography>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
};
